import { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext(null);

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Check for existing token on mount
    useEffect(() => {
        const token = localStorage.getItem("hirrd_token");
        if (token) {
            fetchMe(token);
        } else {
            setLoading(false);
        }
    }, []);

    async function fetchMe(token) {
        try {
            const res = await fetch(`${API}/api/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setUser(data);
            } else {
                localStorage.removeItem("hirrd_token");
            }
        } catch {
            localStorage.removeItem("hirrd_token");
        } finally {
            setLoading(false);
        }
    }

    async function loginEngineer(email, password) {
        const res = await fetch(`${API}/api/auth/engineer/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Login failed");
        localStorage.setItem("hirrd_token", data.access_token);
        setUser(data.user);
        return data;
    }

    async function signupEngineer(formData) {
        const res = await fetch(`${API}/api/auth/engineer/signup`, {
            method: "POST",
            body: formData, // FormData for file upload
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Signup failed");
        localStorage.setItem("hirrd_token", data.access_token);
        setUser(data.user);
        return data;
    }

    async function sendMagicLink(email, companyName) {
        const res = await fetch(`${API}/api/auth/company/magic-link`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, company_name: companyName }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed to send magic link");
        return data;
    }

    async function verifyMagicLink(token, email, company) {
        const res = await fetch(
            `${API}/api/auth/company/magic-link/verify?token=${token}&email=${email}&company=${company}`
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Invalid magic link");
        localStorage.setItem("hirrd_token", data.access_token);
        setUser(data.user);
        return data;
    }

    function loginWithToken(token) {
        localStorage.setItem("hirrd_token", token);
        fetchMe(token);
    }

    function logout() {
        localStorage.removeItem("hirrd_token");
        setUser(null);
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                isAuthenticated: !!user,
                loginEngineer,
                signupEngineer,
                sendMagicLink,
                verifyMagicLink,
                loginWithToken,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
